"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Sparkles, Droplets } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function EightOfCupsPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-blue-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Kadehler</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Sekiz Kadeh</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Sekiz Kadeh</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-blue-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/eight-of-cups.svg"
                  alt="Eight of Cups Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-700 rounded-full flex items-center justify-center">
                  <Droplets className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Eight of Cups</h1>
                  <p className="text-xl text-blue-400">Sekiz Kadeh / Terk Etme ve Arayış</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Geride bırakma, arayış yolculuğu ve ruhsal gelişimin kartı. Mevcut durumdan
                memnuniyetsizlik ve daha derin anlam arayışı.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Terk etme</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Arayış</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Yolculuk</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Ruhsal gelişim</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhsal Arayış</h3>
                  <p className="text-slate-400 text-sm">Daha derin anlam ve amaç arayışı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Cesur Karar</h3>
                  <p className="text-slate-400 text-sm">Zor ama doğru seçimler yapma cesareti.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kişisel Büyüme</h3>
                  <p className="text-slate-400 text-sm">Konfor alanından çıkma ve gelişim.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İç Rehberlik</h3>
                  <p className="text-slate-400 text-sm">Sezgileri takip etme ve iç sese kulak verme.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-blue-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Durgunluk</h3>
                  <p className="text-slate-400 text-sm">Arayıştan vazgeçme ve mevcut duruma razı olma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Korku</h3>
                  <p className="text-slate-400 text-sm">Değişimden korkma ve güvenli olanda kalma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kaçınma</h3>
                  <p className="text-slate-400 text-sm">Sorunlarla yüzleşmek yerine kaçma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Geri Dönüş</h3>
                  <p className="text-slate-400 text-sm">Eski alışkanlıklara ve duruma geri dönme.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🚶</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yürüyen Figür</h3>
              <p className="text-slate-400 text-sm">Yolculuk, arayış ve ileriye gidiş.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Droplets className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Sekiz Kadeh</h3>
              <p className="text-slate-400 text-sm">Geride bırakılan duygusal bağlar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌙</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ay</h3>
              <p className="text-slate-400 text-sm">Sezgi, iç rehberlik ve gece yolculuğu.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏔️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dağlar</h3>
              <p className="text-slate-400 text-sm">Zorluklardan aşma ve yüksek hedefler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🥾</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Asa</h3>
              <p className="text-slate-400 text-sm">Destek, rehberlik ve kararlılık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🎭</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kırmızı Elbise</h3>
              <p className="text-slate-400 text-sm">Tutku, cesaret ve kararlı hareket.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişkiyi sonlandırma</li>
                <li>• Duygusal mesafe koyma</li>
                <li>• Yeni aşk arayışı</li>
                <li>• Bağımsızlık isteği</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İşten ayrılma</li>
                <li>• Kariyer değişimi</li>
                <li>• Yeni fırsatlar arayışı</li>
                <li>• Mesleki tatminsizlik</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal yolculuk</li>
                <li>• Konfor alanından çıkma</li>
                <li>• İç çağrıyı dinleme</li>
                <li>• Kişisel dönüşüm</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Droplets className="h-5 w-5 text-blue-400" />
                Sağlık ve Yaşam
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yaşam tarzı değişimi</li>
                <li>• Detoks ve arınma</li>
                <li>• Fiziksel aktivite artışı</li>
                <li>• Stresli ortamları terk etme</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/cups/seven-of-cups"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Yedi Kadeh
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/cups/nine-of-cups"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Dokuz Kadeh
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}